import React from 'react';
import { Modal, Button, Tabs, Space, Input, InputNumber, Row, Col, Alert, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

const { Text } = Typography;

const LotSearchModal = ({
  visible,
  onClose,
  searchMode,
  setSearchMode,
  simpleSearchQuery,
  setSimpleSearchQuery,
  advancedSearch,
  setAdvancedSearch,
  getFilteredAndSortedLots
}) => {
  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SearchOutlined style={{ color: '#1890ff' }} />
          <span>Search & Filter Lots</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button
          key="clear"
          onClick={() => {
            if (searchMode === 'simple') {
              setSimpleSearchQuery('');
            } else {
              setAdvancedSearch({
                title: '',
                description: '',
                lotNumber: '',
                minPrice: '',
                maxPrice: ''
              });
            }
          }}
        >
          Clear
        </Button>,
        <Button key="apply" type="primary" onClick={onClose}>
          Apply
        </Button>
      ]}
      width={600}
      centered
      maskStyle={{
        backdropFilter: 'blur(1px)',
        WebkitBackdropFilter: 'blur(1px)',
        backgroundColor: 'rgba(0, 0, 0, 0.45)'
      }}
    >
      <Tabs
        activeKey={searchMode}
        onChange={setSearchMode}
        items={[
          {
            key: 'simple',
            label: 'Simple Search',
            children: (
              <div style={{ padding: '8px 0' }}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                      Search Lots
                    </Text>
                    <Input
                      placeholder="Search by title, description, or lot number..."
                      value={simpleSearchQuery}
                      onChange={(e) => setSimpleSearchQuery(e.target.value)}
                      prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                      allowClear
                      size="large"
                    />
                    <div style={{ marginTop: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        💡 Tip: Search across lot titles, descriptions, and lot numbers all at once
                      </Text>
                    </div>
                  </div>

                  {simpleSearchQuery && (
                    <Alert
                      message={`Found ${getFilteredAndSortedLots().length} lot(s) matching "${simpleSearchQuery}"`}
                      type="info"
                      showIcon
                    />
                  )}
                </Space>
              </div>
            )
          },
          {
            key: 'advanced',
            label: 'Advanced Search',
            children: (
              <div style={{ padding: '8px 0' }}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                      Search by Title
                    </Text>
                    <Input
                      placeholder="Enter lot title..."
                      value={advancedSearch.title}
                      onChange={(e) =>
                        setAdvancedSearch({ ...advancedSearch, title: e.target.value })
                      }
                      prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                      allowClear
                    />
                  </div>

                  <div>
                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                      Search by Description
                    </Text>
                    <Input.TextArea
                      placeholder="Enter keywords from description..."
                      value={advancedSearch.description}
                      onChange={(e) =>
                        setAdvancedSearch({ ...advancedSearch, description: e.target.value })
                      }
                      rows={3}
                      allowClear
                    />
                  </div>

                  <div>
                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                      Search by Lot Number
                    </Text>
                    <Input
                      placeholder="Enter lot number..."
                      value={advancedSearch.lotNumber}
                      onChange={(e) =>
                        setAdvancedSearch({ ...advancedSearch, lotNumber: e.target.value })
                      }
                      prefix={<span style={{ color: '#bfbfbf' }}>#</span>}
                      allowClear
                    />
                  </div>

                  <div>
                    <Text strong style={{ display: 'block', marginBottom: '12px' }}>
                      Price Range (PKR)
                    </Text>
                    <Row gutter={12}>
                      <Col span={12}>
                        <div style={{ marginBottom: '4px' }}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            Minimum Price
                          </Text>
                        </div>
                        <InputNumber
                          style={{ width: '100%' }}
                          placeholder="Min price"
                          value={
                            advancedSearch.minPrice
                              ? parseFloat(advancedSearch.minPrice)
                              : undefined
                          }
                          onChange={(value) =>
                            setAdvancedSearch({
                              ...advancedSearch,
                              minPrice: value ? value.toString() : ''
                            })
                          }
                          min={0}
                          formatter={(value) => {
                            if (!value) return '';
                            const numStr = typeof value === 'string' ? value : value.toString();
                            return `PKR ${numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
                          }}
                          parser={(value) => value.replace(/PKR\s?|(,*)/g, '')}
                        />
                      </Col>
                      <Col span={12}>
                        <div style={{ marginBottom: '4px' }}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            Maximum Price
                          </Text>
                        </div>
                        <InputNumber
                          style={{ width: '100%' }}
                          placeholder="Max price"
                          value={
                            advancedSearch.maxPrice
                              ? parseFloat(advancedSearch.maxPrice)
                              : undefined
                          }
                          onChange={(value) =>
                            setAdvancedSearch({
                              ...advancedSearch,
                              maxPrice: value ? value.toString() : ''
                            })
                          }
                          min={0}
                          formatter={(value) => {
                            if (!value) return '';
                            const numStr = typeof value === 'string' ? value : value.toString();
                            return `PKR ${numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
                          }}
                          parser={(value) => value.replace(/PKR\s?|(,*)/g, '')}
                        />
                      </Col>
                    </Row>
                  </div>

                  {(advancedSearch.title ||
                    advancedSearch.description ||
                    advancedSearch.lotNumber ||
                    advancedSearch.minPrice ||
                    advancedSearch.maxPrice) && (
                    <Alert
                      message={`Found ${getFilteredAndSortedLots().length} lot(s) matching your search criteria`}
                      type="info"
                      showIcon
                      style={{ marginTop: '8px' }}
                    />
                  )}
                </Space>
              </div>
            )
          }
        ]}
      />
    </Modal>
  );
};

export default LotSearchModal;
